import { createContext, useContext } from 'react';
import type React from 'react';
import type { UiComponentSnapshotItem } from '../utils/uiComponentTracker';

export interface RequestDetails {
  url: string;
  method: string;
  body: unknown;
  response?: {
    status: number;
    statusText: string;
  };
  isOffline?: boolean;
}

export type IssueType = 'BUG' | 'Anfrage' | 'Feature';

export interface ErrorContextType {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  requestDetails: RequestDetails | null;
  serverErrorDetails: unknown | null;
  uiSnapshot: UiComponentSnapshotItem[];
  issueType: IssueType;
  setIssueType: React.Dispatch<React.SetStateAction<IssueType>>;
  setError: (error: Error | null, errorInfo?: React.ErrorInfo) => void;
  setRequestDetails: (details: RequestDetails | null) => void;
  setServerErrorDetails: (details: unknown | null) => void;
  isModalOpen: boolean;
  openModal: (forceScreenshot?: boolean) => void;
  closeModal: () => void;
  screenshot: string | null;
  captureScreenshot: () => Promise<string | null>;
  description: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  isOffline: boolean;
  retryCountdown: number;
  isSendingInBackground: boolean;
  hasSendError: boolean;
  sendErrorReport: (withScreenshot: boolean, screenshotData?: string | null) => Promise<void>;
}

export const ErrorContext = createContext<ErrorContextType>({
  error: null,
  errorInfo: null,
  requestDetails: null,
  serverErrorDetails: null,
  uiSnapshot: [],
  issueType: 'BUG',
  setIssueType: () => {},
  setError: () => {},
  setRequestDetails: () => {},
  setServerErrorDetails: () => {},
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  screenshot: null,
  captureScreenshot: async () => null,
  description: '',
  setDescription: () => {},
  isOffline: false,
  retryCountdown: 0,
  isSendingInBackground: false,
  hasSendError: false,
  sendErrorReport: async () => {},
});

export const useErrorContext = () => useContext(ErrorContext);
